import parse from 'html-react-parser';
import React from 'react';
import { checkLocalStorage, getLocalStorage } from '../services/storage/storage-service';
import { MappingRoles, RolePermission, SystemLogFileTypes } from './constants';
import { getUserRoleType } from '../services/aws/aws-services';

/**
 * @description Function to check if a number fall in a range
 * @param num Number to be checked
 * @param min Min value in the range
 * @param max Max value in the range
 * @returns Boolean. True if num lies between min and max
 */
export const between = (num, min, max) => {
	return num >= min && num < max;
}

/**
 * @description Function to check if a number fall in a hard range 
 * @description (at most, equal to both the extremes)
 * @param num Number to be checked
 * @param min Min value in the range
 * @param max Max value in the range
 * @returns Boolean. True if num lies between min and max
 */
export const hardBetween = (num, min, max) => {
	return num >= min && num <= max;
}

/**
 * @description Function to format and breakdown date objects
 * @param date JS Date object
 * @returns Object with date time attributes broken down
 */
export const breakDownDateFormat = (date) => {
	let newDate = date ? new Date(date) : new Date();
	let d = newDate.getDate();
	let m = newDate.getMonth() + 1;
	let yyyy = newDate.getFullYear();
	let dd = d > 9 ? d.toString() : '0' + d.toString();
	let mm = m > 9 ? m.toString() : '0' + m.toString();
	let hr = newDate.getHours() > 9 ? newDate.getHours().toString() : '0' + newDate.getHours().toString();
	let min = newDate.getMinutes() > 9 ? newDate.getMinutes().toString() : '0' + newDate.getMinutes().toString();
	let sec = newDate.getSeconds() > 9 ? newDate.getSeconds().toString() : '0' + newDate.getSeconds().toString();

	return {
		date: dd,
		month: mm,
		fullYear: yyyy,
		hour: hr,
		min: min,
		sec: sec
	}
}

/**
 * @description Function to format date time
 * @param date Date to be formatted
 * @returns Formatted date
 */
export const formatDateTime = (date) => {
	let correctInput = date;
	let newDate = new Date(correctInput);
	let convDateTime = '';
	if (!isNaN(newDate.getTime())) {
		let d = newDate.getUTCDate();
		let m = newDate.getUTCMonth() + 1;
		let yyyy = newDate.getUTCFullYear();
		let dd = d > 9 ? d.toString() : '0' + d.toString();
		let mm = m > 9 ? m.toString() : '0' + m.toString();
		let hr = newDate.getUTCHours() > 9 ? newDate.getUTCHours().toString() : '0' + newDate.getUTCHours().toString();
		let min = newDate.getUTCMinutes() > 9 ? newDate.getUTCMinutes().toString() : '0' + newDate.getUTCMinutes().toString();
		let sec = newDate.getUTCSeconds() > 9 ? newDate.getUTCSeconds().toString() : '0' + newDate.getUTCSeconds().toString();
		convDateTime = yyyy + '-' + mm + '-' + dd + ' ' + hr + ':' + min + ':' + sec;
	}
	return convDateTime;
}

/**
 * @description Function to format date time for CSV filename
 * @returns Formatted date
 */
export const formatDateTimeForCSV = () => {
	let formattedDateObj = breakDownDateFormat();
	const { date, month, fullYear, hour, min, sec } = formattedDateObj;
	return `${month}${date}${fullYear}_${hour}${min}${sec}`;
}

/**
 * @description Function to format date
 * @param dateObj Date to be formatted
 * @returns Formatted date
 */
export const formatDate = (dateObj) => {
	let formattedDateObj = breakDownDateFormat(dateObj);
	const { date, month, fullYear } = formattedDateObj;

	return `${fullYear}-${month}-${date}`;
}

/**
 * @description Function to format date to string
 * @param date Date to be formatted
 * @returns Formatted date
 */
export const formatDateString = (date) => {
	const dateString = new Date(date).toDateString();
	const dateArray = dateString.split(' ');
	var stringValue = '';
	if (dateArray.length) {
		stringValue += `${dateArray[2]} ${dateArray[1]} ${dateArray[3]}`
	}

	return stringValue;
}

/**
 * @description Function to convert UTC to Local time 
 * @param dateObj Date to be formatted
 * @returns Formatted date
 */
export const convertUTCToLocalDateTime = (dateObj) => {
	let formattedDateObj = breakDownDateFormat(dateObj);
	const { date, month, fullYear, hour, min, sec } = formattedDateObj;

	return `${fullYear}-${month}-${date} ${hour}:${min}:${sec}`;
}

/**
 * @description Function to convert date to date-time input format (upto minute)
 * @param dateObj Date to be formatted
 */
export const getTodaysDateTime = (dateObj) => {
	let formattedDateObj = breakDownDateFormat(dateObj);
	const { date, month, fullYear, hour, min } = formattedDateObj;

	return `${fullYear}-${month}-${date} ${hour}:${min}`;
}

/**
 * @description Function to round up decimal values as whole numbers
 * @param num Float value to be rounded
 * @returns Whole number value rounded off
 */
export const wholeNumberRoundOff = (num, isGraph = false) => {
	if (isNaN(parseFloat(num))) {
		return isGraph ? null : 'N/A';
	}

	return Math.round(num);
}

/**
 * @description Function to round up up to certain decimal places
 * @param num Float value to be rounded
 * @returns Floating point value rounded off
 */
export const customToFixed = (num, decimalPlcaes) => {
	const floatNum = parseFloat(num);
	if (isNaN(floatNum)) {
		return 0;
	}

	return parseFloat(floatNum.toFixed(decimalPlcaes));
}

/**
 * @description Function to check if a number is negative or not
 * @param num Float value to be checked
 * @returns Boolean (true if negative, false if not)
 */
export const isNegative = (num) => {
	if (isNaN(parseFloat(num))) {
		return false;
	}

	let sign = Math.sign(num);
	switch (sign) {
		case -1:
			return true;
		case 0:
		case -0:
			return 1 / sign === -Infinity;
		default:
			return false;
	}
}

/**
 * @description Function to parse HTML string to DOM elements
 * @param str String in question
 * @returns String value to be displayed
 */
export const parseHTML = (htmlString) => {
	return parse(htmlString && typeof htmlString === 'string' ? htmlString : '');
}

/**
 * @description Function to disintegrate an array into a string
 * @param array array in question
 * @returns String value to be displayed
 */
export const arrayToString = (array, delimiter) => {
	return array && Array.isArray(array) ? array.join(delimiter ? delimiter : ', ') : array;
}

/**
 * @description Function to convert HTML to text string
 * @param html HTML in question
 * @returns String value to be displayed
 */
export const convertToPlainText = (html) => {
	var tempDivElement = document.createElement("div"); // Create a new div element	
	tempDivElement.innerHTML = html; // Set the HTML content with the given value

	return tempDivElement.textContent || tempDivElement.innerText || ""; // Retrieve and return the text property of the element
}

/**
 * @description Function to ensure positive number inputs
 * @param e Keydown event
 * @returns Boolean
 */
export const isPositiveNumber = (e) => {
	// 48-57 => number row numbers, 96-105 => numpad numbers
	// 8 => backspace, 110 => numpad decimal point, 37-40 => arrow keys
	// 190 => decimal point, 46 => delete, 13 => enter
	// 86 => v, 88 => x, 67 => c
	if (!((e.keyCode > 95 && e.keyCode < 106)
		|| (e.keyCode > 47 && e.keyCode < 58)
		|| (e.keyCode > 36 && e.keyCode < 41)
		|| e.keyCode === 8 || e.keyCode === 110
		|| e.keyCode === 190 || e.keyCode === 46
		|| (e.ctrlKey && e.keyCode === 67)
		|| (e.ctrlKey && e.keyCode === 86)
		|| (e.ctrlKey && e.keyCode === 88)
		|| e.keyCode === 13)) {
		return false;
	}

	return true;
}

/**
 * @description Function to convert string to title case
 * @param str String to be converted
 * @returns Converted string
 */
export const toTitleCase = (str) => {
	if (!str || !str.toString().trim()) {
		return 'N/A';
	}
	return str.replace(
		/\w\S*/g,
		(txt) => {
			return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
		}
	);
}

/**
 * @description Function to get concatenated name if availabled
 * @param firstName First name
 * @param lastName Last name
 * @param midName Middle name
 * @returns Converted string
 */
export const getConcatenatedName = (firstName, lastName, midName = '') => {
	if (!firstName && !lastName && !midName) {
		return 'N/A';
	}

	return `${firstName ? firstName : ''} ${midName ? midName : ''} ${lastName ? lastName : ''}`;
}

/**
 * @description Function to get N/A help text
 * @returns JSX template with said help text
 */
export const NAHelpText = () => {
	return (
		<div className="my-2 na-help-text">
			<p className="m-0">*N/A - Not Available</p>
		</div>
	);
}

export const getJsonFromUrl = (str) => {
	const query = str.substr(1);
	const result = {};

	query.split('&').forEach(function (part) {
		const item = part.split('=');
		result[item[0]] = decodeURIComponent(item[1]);
	});

	return result;
};

export const isFederatedCallback = () => {
	return window.location.hash ? true : false;
}

export const isFederatedLogin = () => {
	return checkLocalStorage('userType') && getLocalStorage('userType') === 'AD_User';
}

export const getTokenObjFromCallbackURL = () => {
	return window.location.hash ? getJsonFromUrl(window.location.hash) : null;
}

export const getTokenFromCallbackURL = () => {
	const tokenObj = getTokenObjFromCallbackURL();
	return tokenObj?.id_token || null;
}

/**
 * @description Function to get if the user is logged in
 * @returns Boolean true if yes, false if not
 */
export const isAuthenticated = () => {
	return typeof getTokenFromCallbackURL() === 'string' || checkLocalStorage('cognito_data');
}

/**
 * @description Function to get if this is the user's first login
 * @returns Boolean true if yes, false if not
 */
export const isFirstLogin = () => {
	return checkLocalStorage('isFirstLogin');
}

/**
 * @description Function to return the image src as base64 encoded string
 * @returns String
 */
export const getImgSrc = (img) => {
	var defaultExport = require(`../assets/images/${img}`);
	return defaultExport.default ? defaultExport.default : defaultExport;
}

/**
 * @description Function to define template for loading animation
 * @returns Template for loading animation
 */
export const getLoadingTemplate = () => {
	return <div className="loading-img mb-3"><img className="" src={getImgSrc('loading.gif')} alt="loading" /></div>
}

/**
 * @description Function to check for whitespace at the start
 * @param value Input value
 * @returns Regex test result (Boolean)
 */
export const validateWhiteSpace = (value) => {
	return new RegExp(/^\s/g).test(value);
}

/**
 * @description Function to convert bytes to KB or MB
 * @param value Input value
 * @returns Converted value
 */
export const convertFileSize = (value, unit) => {
	if (!value) {
		return null;
	}

	switch (unit && unit.toLowerCase()) {
		case 'kb':
			return Math.ceil(value / 1024);
		case 'mb':
			return Math.ceil(value / (1024 * 1024));

		default:
			return Math.ceil(value / 1024);
	}

}

/**
 * @description Function to show device type name
 * @param deviceType String default value
 * @returns String in uppercase
 */
export const getDeviceTypeName = (deviceType) => {
	if (!deviceType) {
		return 'N/A';
	}
	let deviceTypeUppercase = deviceType.toUpperCase();
	switch (deviceTypeUppercase) {
		case 'ORCA':
			return 'CORI-XT';

		default:
			return deviceTypeUppercase;
	}
}

/**
 * @description Function to get DAP role name
 * @param role String default value
 * @returns String in uppercase
 */
export const getDAPRoleName = (role) => {
	if (!role) {
		return 'N/A';
	}
	let roleName = MappingRoles.find(x => x.userRole === role);
	return roleName?.specialLabel || role;
}

/**
 * @description Function to get icon for a filetype
 * @param filename String nameof the file
 * @param options Options for the icon
 * @returns SVG icon
 */
export const getFileIcon = (filename, options = { height: "1.5rem", width: "1.5rem" }) => {
	let defaultIcon = (
		<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-file-earmark" viewBox="0 0 16 16">
			<path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z" />
		</svg>
	)
	if (!filename) {
		return defaultIcon;
	}

	let filenameBreakdown = filename.split('.');
	let fileExtension = filenameBreakdown.length ? filenameBreakdown[filenameBreakdown.length - 1] : ''

	if (!fileExtension) {
		return defaultIcon;
	}

	switch (fileExtension) {
		case SystemLogFileTypes.archive.zip:
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-file-zip" viewBox="0 0 16 16">
					<path d="M6.5 7.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v.938l.4 1.599a1 1 0 0 1-.416 1.074l-.93.62a1 1 0 0 1-1.109 0l-.93-.62a1 1 0 0 1-.415-1.074l.4-1.599zm2 0h-1v.938a1 1 0 0 1-.03.243l-.4 1.598.93.62.93-.62-.4-1.598a1 1 0 0 1-.03-.243z" />
					<path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm5.5-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9v1H8v1h1v1H8v1h1v1H7.5V5h-1V4h1V3h-1V2h1z" />
				</svg>
			)

		case SystemLogFileTypes.document.json:
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-filetype-json" viewBox="0 0 16 16">
					<path fillRule="evenodd" d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM4.151 15.29a1.2 1.2 0 0 1-.111-.449h.764a.58.58 0 0 0 .255.384q.105.073.25.114.142.041.319.041.245 0 .413-.07a.56.56 0 0 0 .255-.193.5.5 0 0 0 .084-.29.39.39 0 0 0-.152-.326q-.152-.12-.463-.193l-.618-.143a1.7 1.7 0 0 1-.539-.214 1 1 0 0 1-.352-.367 1.1 1.1 0 0 1-.123-.524q0-.366.19-.639.192-.272.528-.422.337-.15.777-.149.456 0 .779.152.326.153.5.41.18.255.2.566h-.75a.56.56 0 0 0-.12-.258.6.6 0 0 0-.246-.181.9.9 0 0 0-.37-.068q-.324 0-.512.152a.47.47 0 0 0-.185.384q0 .18.144.3a1 1 0 0 0 .404.175l.621.143q.326.075.566.211a1 1 0 0 1 .375.358q.135.222.135.56 0 .37-.188.656a1.2 1.2 0 0 1-.539.439q-.351.158-.858.158-.381 0-.665-.09a1.4 1.4 0 0 1-.478-.252 1.1 1.1 0 0 1-.29-.375m-3.104-.033a1.3 1.3 0 0 1-.082-.466h.764a.6.6 0 0 0 .074.27.5.5 0 0 0 .454.246q.285 0 .422-.164.137-.165.137-.466v-2.745h.791v2.725q0 .66-.357 1.005-.355.345-.985.345a1.6 1.6 0 0 1-.568-.094 1.15 1.15 0 0 1-.407-.266 1.1 1.1 0 0 1-.243-.39m9.091-1.585v.522q0 .384-.117.641a.86.86 0 0 1-.322.387.9.9 0 0 1-.47.126.9.9 0 0 1-.47-.126.87.87 0 0 1-.32-.387 1.55 1.55 0 0 1-.117-.641v-.522q0-.386.117-.641a.87.87 0 0 1 .32-.387.87.87 0 0 1 .47-.129q.265 0 .47.129a.86.86 0 0 1 .322.387q.117.255.117.641m.803.519v-.513q0-.565-.205-.973a1.46 1.46 0 0 0-.59-.63q-.38-.22-.916-.22-.534 0-.92.22a1.44 1.44 0 0 0-.589.628q-.205.407-.205.975v.513q0 .562.205.973.205.407.589.626.386.217.92.217.536 0 .917-.217.384-.22.589-.626.204-.41.205-.973m1.29-.935v2.675h-.746v-3.999h.662l1.752 2.66h.032v-2.66h.75v4h-.656l-1.761-2.676z" />
				</svg>
			)

		case SystemLogFileTypes.images.jpeg:
		case SystemLogFileTypes.images.jpg:
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-filetype-jpg" viewBox="0 0 16 16">
					<path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zm-4.34 8.132q.114.23.14.492h-.776a.8.8 0 0 0-.097-.249.7.7 0 0 0-.17-.19.7.7 0 0 0-.237-.126 1 1 0 0 0-.299-.044q-.428 0-.665.302-.234.301-.234.85v.498q0 .351.097.615a.9.9 0 0 0 .304.413.87.87 0 0 0 .519.146 1 1 0 0 0 .457-.096.67.67 0 0 0 .272-.264q.09-.164.091-.363v-.255H8.24v-.59h1.576v.798q0 .29-.097.55a1.3 1.3 0 0 1-.293.458 1.4 1.4 0 0 1-.495.313q-.296.111-.697.111a2 2 0 0 1-.753-.132 1.45 1.45 0 0 1-.533-.377 1.6 1.6 0 0 1-.32-.58 2.5 2.5 0 0 1-.105-.745v-.506q0-.543.2-.95.201-.406.582-.633.384-.228.926-.228.357 0 .636.1.28.1.48.275t.314.407ZM0 14.786q0 .246.082.465.083.22.243.39.165.17.407.267.246.093.569.093.63 0 .984-.345.357-.346.358-1.005v-2.725h-.791v2.745q0 .303-.138.466t-.422.164a.5.5 0 0 1-.454-.246.6.6 0 0 1-.073-.27H0Zm4.92-2.86H3.322v4h.791v-1.343h.803q.43 0 .732-.172.305-.177.463-.475.162-.302.161-.677 0-.374-.158-.677a1.2 1.2 0 0 0-.46-.477q-.3-.18-.732-.179Zm.546 1.333a.8.8 0 0 1-.085.381.57.57 0 0 1-.238.24.8.8 0 0 1-.375.082H4.11v-1.406h.66q.327 0 .512.182.185.181.185.521Z" />
				</svg>
			)
		case SystemLogFileTypes.images.png:
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-filetype-png" viewBox="0 0 16 16">
					<path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2v-1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zm-3.76 8.132q.114.23.14.492h-.776a.8.8 0 0 0-.097-.249.7.7 0 0 0-.17-.19.7.7 0 0 0-.237-.126 1 1 0 0 0-.299-.044q-.427 0-.665.302-.234.301-.234.85v.498q0 .351.097.615a.9.9 0 0 0 .304.413.87.87 0 0 0 .519.146 1 1 0 0 0 .457-.096.67.67 0 0 0 .272-.264q.09-.164.091-.363v-.255H8.82v-.59h1.576v.798q0 .29-.097.55a1.3 1.3 0 0 1-.293.458 1.4 1.4 0 0 1-.495.313q-.296.111-.697.111a2 2 0 0 1-.753-.132 1.45 1.45 0 0 1-.533-.377 1.6 1.6 0 0 1-.32-.58 2.5 2.5 0 0 1-.105-.745v-.506q0-.543.2-.95.201-.406.582-.633.384-.228.926-.228.357 0 .636.1.281.1.48.275.2.176.314.407Zm-8.64-.706H0v4h.791v-1.343h.803q.43 0 .732-.172.305-.177.463-.475a1.4 1.4 0 0 0 .161-.677q0-.374-.158-.677a1.2 1.2 0 0 0-.46-.477q-.3-.18-.732-.179m.545 1.333a.8.8 0 0 1-.085.381.57.57 0 0 1-.238.24.8.8 0 0 1-.375.082H.788v-1.406h.66q.327 0 .512.182.185.181.185.521m1.964 2.666V13.25h.032l1.761 2.675h.656v-3.999h-.75v2.66h-.032l-1.752-2.66h-.662v4z" />
				</svg>
			)

		default:
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width={options.width} height={options.height} fill="currentColor" className="bi bi-file-earmark-code" viewBox="0 0 16 16">
					<path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z" />
					<path d="M8.646 6.646a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L10.293 9 8.646 7.354a.5.5 0 0 1 0-.708m-1.292 0a.5.5 0 0 0-.708 0l-2 2a.5.5 0 0 0 0 .708l2 2a.5.5 0 0 0 .708-.708L5.707 9l1.647-1.646a.5.5 0 0 0 0-.708" />
				</svg>
			)
	}
}

/**
 * @description Function to check and restrict the employee ID
 * @param e KeyDown event
 * @returns Boolean
 */
export const checkEmployeeIDLength = (e) => {
	const KEYS = {
		leftKey: 37,
		rightKey: 39,
		backspace: 8,
		deleteKey: 46,
		digitZero: 48,
		digitNine: 57,
		numPadZero: 96,
		numPadNine: 105
	};
	if (
		e.keyCode === KEYS.backspace ||
		e.keyCode === KEYS.deleteKey ||
		e.keyCode === KEYS.rightKey ||
		e.keyCode === KEYS.leftKey) {
		return true;
	}

	if (!(
		hardBetween(e.keyCode, KEYS.digitZero, KEYS.digitNine) ||
		hardBetween(e.keyCode, KEYS.numPadZero, KEYS.numPadNine)
	) ||
		e.target.value.length === e.target.maxLength ||
		e.shiftKey) {
		e.stopPropagation();
		e.preventDefault();
		return false;
	}
	return true;
}

/**
 * @description Function to get if the user has permission
 * @returns Boolean true if yes, false if not
 */
export const userHasPermission = (permissionModule, userRole = null) => {
	if (!userRole) {
		userRole = getUserRoleType();
	}
	var permissionFlag = false;
	if (RolePermission[permissionModule].includes(userRole)) {
		permissionFlag = true;
	}
	return permissionFlag;
}